import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ICON_COLOR_CLASSES, type MaximoDocType, type SyncPreset } from "~/utils/maximo-sync";
import { Button, Card, Switch, Tag, Text } from "~/components/ui";

type MaximoPresetCardProps = {
  preset: SyncPreset;
  withVision: boolean;
  onWithVisionChange: (value: boolean) => void;
  syncingType: MaximoDocType | "all" | null;
  onSync: (preset: SyncPreset) => void;
};

export function MaximoPresetCard({
  preset,
  withVision,
  onWithVisionChange,
  syncingType,
  onSync,
}: MaximoPresetCardProps) {
  const { t } = useTranslation("admin");

  return (
    <Card hoverable className="h-full" bodyClassName="flex h-full flex-col">
      {/* Header: Icon + Title */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`text-2xl ${ICON_COLOR_CLASSES[preset.color]} shrink-0`}>{preset.icon}</div>
        <div className="min-w-0">
          <Text strong className="block">
            {t(`uploadAdmin.sync.maximo.presets.${preset.key}.title`)}
          </Text>
          <Tag color={preset.color} className="mt-1">
            {preset.schemaId}
          </Tag>
        </div>
      </div>

      {/* Description */}
      <Text tone="muted" className="text-sm flex-1">
        {t(`uploadAdmin.sync.maximo.presets.${preset.key}.description`)}
      </Text>

      {/* Vision toggle for doclink */}
      {preset.defaultVision && (
        <div className="p-2 bg-noa-hover rounded mb-4">
          <div className="flex items-center justify-between">
            <Text className="text-xs">{t("uploadAdmin.form.withVision")}</Text>
            <Switch
              size="small"
              checked={withVision}
              onChange={onWithVisionChange}
              aria-label={t("uploadAdmin.form.withVision")}
            />
          </div>
          {!withVision && (
            <div className="text-xs text-orange-600 mt-1">⚠️ {t("uploadAdmin.sync.maximo.visionWarning")}</div>
          )}
        </div>
      )}

      {/* Button pinned to bottom */}
      <Button
        variant="primary"
        icon={<RefreshCw size={14} className={syncingType === preset.key ? "animate-spin" : undefined} />}
        loading={syncingType === preset.key}
        onClick={() => onSync(preset)}
        block
        data-testid={`maximo-sync-${preset.key}-button`}
      >
        {t("uploadAdmin.sync.maximo.sync")}
      </Button>
    </Card>
  );
}
